import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFactory } from "@/core/hooks/useFactory";
import { useLanguage } from "@/core/LanguageContext";
import { useToast } from "@/shared/hooks/use-toast";
import {
  fetchSupplierIncomingResultsBySupplier,
  type SupplierIncomingResult,
} from "@/modules/supplier-quality/api";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/dialog";
import { ArrowLeft, Building2, FileText, Calendar, Pencil } from "lucide-react";
import { format, subDays } from "date-fns";

interface SupplierDetail {
  id: string;
  name: string;
  code: string;
  contact_email: string | null;
  created_at: string;
}

interface SupplierFormData {
  name: string;
  code: string;
  contact_email: string;
}

interface SupplierClaim {
  id: string;
  claim_number: string;
  title: string;
  status: string;
  created_at: string;
}

type InspectionResult = SupplierIncomingResult;

const statusColors: Record<string, string> = {
  open: "bg-warning/20 text-warning border-warning/30",
  in_progress: "bg-chart-2/20 text-chart-2 border-chart-2/30",
  pending_supplier: "bg-chart-3/20 text-chart-3 border-chart-3/30",
  pending_approval: "bg-chart-4/20 text-chart-4 border-chart-4/30",
  closed: "bg-success/20 text-success border-success/30",
};

export default function SupplierQualityDetail() {
  const { supplierId } = useParams();
  const { selectedFactoryId, selectedFactory } = useFactory();
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<SupplierFormData>({
    name: "",
    code: "",
    contact_email: "",
  });

  const supplierDetailQueryKey = [
    "supplier-detail",
    supplierId,
    selectedFactoryId,
    selectedFactory?.tenant_id ?? null,
  ] as const;
  const suppliersQueryKey = [
    "suppliers",
    selectedFactoryId,
    selectedFactory?.tenant_id ?? null,
  ] as const;

  const { data: supplier, isLoading: isLoadingSupplier } = useQuery({
    queryKey: supplierDetailQueryKey,
    queryFn: async () => {
      if (!supplierId || !selectedFactoryId || !selectedFactory?.tenant_id) return null;
      const { data, error } = await supabase
        .schema("mod_8d")
        .from("suppliers")
        .select("id, name, code, contact_email, created_at")
        .eq("id", supplierId)
        .eq("tenant_id", selectedFactory.tenant_id)
        .eq("factory_id", selectedFactoryId)
        .maybeSingle();
      if (error) throw error;
      return data as SupplierDetail | null;
    },
    enabled: !!supplierId && !!selectedFactoryId,
  });

  const openEditDialog = () => {
    if (!supplier) return;
    setFormData({
      name: supplier.name,
      code: supplier.code,
      contact_email: supplier.contact_email || "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!supplierId || !selectedFactory?.id || !selectedFactory?.tenant_id) {
        throw new Error(t("mod_8d.supplierQuality.errors.noFactory", "No factory selected"));
      }

      const { error } = await supabase
        .schema("mod_8d")
        .from("suppliers")
        .update({
          name: formData.name,
          code: formData.code,
          contact_email: formData.contact_email || null,
        })
        .eq("id", supplierId)
        .eq("tenant_id", selectedFactory.tenant_id)
        .eq("factory_id", selectedFactory.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierDetailQueryKey });
      queryClient.invalidateQueries({ queryKey: suppliersQueryKey });
      toast({
        title: t("mod_8d.supplierQuality.toast.supplierUpdated", "Supplier updated"),
      });
      closeDialog();
    },
    onError: (error: Error) => {
      toast({
        title: t("mod_8d.supplierQuality.toast.errorTitle", "Error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const { data: claims = [], isLoading: isLoadingClaims } = useQuery({
    queryKey: ["supplier-claims", supplierId, selectedFactoryId, selectedFactory?.tenant_id ?? null],
    queryFn: async () => {
      if (!supplierId || !selectedFactoryId || !selectedFactory?.tenant_id) return [];
      const { data, error } = await supabase
        .schema("mod_8d")
        .from("claims")
        .select("id, claim_number, title, status, created_at")
        .eq("tenant_id", selectedFactory.tenant_id)
        .eq("factory_id", selectedFactoryId)
        .eq("supplier_id", supplierId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as SupplierClaim[];
    },
    enabled: !!supplierId && !!selectedFactoryId,
  });

  const { data: inspections = [], isLoading: isLoadingInspections } = useQuery({
    queryKey: [
      "mod_supplier_incoming",
      "supplier-inspections",
      selectedFactory?.tenant_id ?? null,
      supplierId,
      selectedFactoryId,
    ],
    queryFn: async () => {
      if (!supplierId || !selectedFactoryId || !selectedFactory?.tenant_id) return [];
      return fetchSupplierIncomingResultsBySupplier(
        selectedFactory.tenant_id,
        selectedFactoryId,
        supplierId,
        10,
      );
    },
    enabled: !!supplierId && !!selectedFactoryId && !!selectedFactory?.tenant_id,
  });

  if (!selectedFactoryId) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Please select a factory first.</p>
      </div>
    );
  }

  if (isLoadingSupplier) {
    return <div className="p-6 text-muted-foreground">Loading supplier...</div>;
  }

  if (!supplier) {
    return <div className="p-6 text-muted-foreground">Supplier not found.</div>;
  }

  const claimsLast7 = claims.filter((claim) => new Date(claim.created_at) >= subDays(new Date(), 7)).length;
  const claimsLast31 = claims.filter((claim) => new Date(claim.created_at) >= subDays(new Date(), 31)).length;

  return (
    <div className="space-y-6 animate-fade-in">
      <Link to="/supplier-quality?tab=suppliers">
        <Button variant="ghost" size="sm" className="gap-2 mb-2">
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back to Supplier Quality</span>
        </Button>
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
            <Building2 className="w-6 h-6 text-primary" />
            {supplier.name}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{supplier.code}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" onClick={openEditDialog}>
              <Pencil className="w-4 h-4 mr-2" />
              {t("mod_8d.supplierQuality.actions.editSupplier", "Edit Supplier")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("mod_8d.supplierQuality.actions.editSupplier", "Edit Supplier")}
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                saveMutation.mutate();
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="supplier-detail-name">
                    {t("mod_8d.supplierQuality.form.companyName", "Company Name *")}
                  </Label>
                  <Input
                    id="supplier-detail-name"
                    value={formData.name}
                    onChange={(event) => setFormData({ ...formData, name: event.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supplier-detail-code">
                    {t("mod_8d.supplierQuality.form.supplierCode", "Supplier Code *")}
                  </Label>
                  <Input
                    id="supplier-detail-code"
                    value={formData.code}
                    onChange={(event) => setFormData({ ...formData, code: event.target.value })}
                    placeholder={t("mod_8d.supplierQuality.form.codePlaceholder", "e.g., SUP001")}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier-detail-contact-email">
                  {t("mod_8d.supplierQuality.form.contactEmail", "Contact Email")}
                </Label>
                <Input
                  id="supplier-detail-contact-email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(event) =>
                    setFormData({ ...formData, contact_email: event.target.value })
                  }
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={closeDialog}>
                  {t("mod_8d.supplierQuality.actions.cancel", "Cancel")}
                </Button>
                <Button type="submit" disabled={saveMutation.isPending}>
                  {t("mod_8d.supplierQuality.actions.update", "Update")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Claims last 7 days</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-bold">{claimsLast7}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Claims last 31 days</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-bold">{claimsLast31}</CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Supplier since</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-bold">
            {format(new Date(supplier.created_at), "MMM d, yyyy")}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Recent Claims
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoadingClaims ? (
              <p className="text-sm text-muted-foreground">Loading claims...</p>
            ) : claims.length === 0 ? (
              <p className="text-sm text-muted-foreground">No claims yet.</p>
            ) : (
              claims.slice(0, 6).map((claim) => (
                <Link key={claim.id} to={`/8d-management?tab=claims&claim=${claim.id}`} className="block">
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 hover:border-primary/40 transition-colors">
                    <div>
                      <p className="text-sm font-medium">
                        {claim.claim_number} · {claim.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(claim.created_at), "MMM d, yyyy")}
                      </p>
                    </div>
                    <Badge variant="outline" className={statusColors[claim.status] || ""}>
                      {claim.status.replace("_", " ")}
                    </Badge>
                  </div>
                </Link>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              Recent Inspections
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoadingInspections ? (
              <p className="text-sm text-muted-foreground">Loading inspections...</p>
            ) : inspections.length === 0 ? (
              <p className="text-sm text-muted-foreground">No inspections found.</p>
            ) : (
              inspections.slice(0, 6).map((inspection) => (
                <div key={inspection.id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">
                      {inspection.material_name || "Unknown material"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {inspection.batch_lot_number ? `Batch ${inspection.batch_lot_number}` : "No batch"} ·
                      {" "}
                      {format(new Date(inspection.created_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <Badge
                    variant={inspection.overall_status === "approved" ? "outline" : "destructive"}
                    className={inspection.overall_status === "approved" ? "border-success/40 text-success" : ""}
                  >
                    {inspection.overall_status === "approved" ? "Approved" : "Rejected"}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
